import {
    ComponentProps,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { DrawerWrapper } from '@/components'
import {
    GetReportResponse,
    useElementWidth,
    useFiltersStore,
    TotalEmissionsReportSettings,
} from '@/hooks'
import { ReportEditHeader } from './ReportEditHeader'
import { mapFiltersToParams } from './mapFiltersToParams'
import { mapParamsToFilters } from './mapParamsToFilters'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'
import {
    hasNameChanged,
    haveFiltersChanged,
    haveSettingsChanged,
} from '../../utils/detectReportChange'
import { SustainabilityFiltersContainer } from '@/features/Sustainability/components/SustainabilityFilters/SustainabilityFilters'
import { TotalEmissionsReport } from '@/features/Sustainability/components/TotalEmissions/TotalEmissionsReport'
import {
    DEFAULT_SUSTAINABILITY_FILTERING,
    DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS,
    DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS,
} from '@/utils/constants'
import { mapSustainabilityFiltersToParams } from './mapSustainabilityFiltersToParams'
import { processSustainabilityFilters } from '../../utils/processSustainabilityFilters'
import { TotalEmissionsDrawer } from '@/features/Sustainability/components/TotalEmissionsDrawer/TotalEmissionsDrawer'
import { TableLoadingOverlay } from '@/components'
import { useTranslation } from 'react-i18next'
import { ElectricityMixUsageDrawer } from '@/features/Sustainability/components/ElectricityMixUsageDrawer/ElectricityMixUsageDrawer'
import useSustainabilityStore from '@/features/Sustainability/stores/useSustainabilityStore/useSustainabilityStore'

type ReportEditTotalEmissionsProps = {
    data: GetReportResponse
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

type OnChangeFn = ComponentProps<
    typeof SustainabilityFiltersContainer
>['onChange']

export const ReportEditTotalEmissions: React.FC<
    ReportEditTotalEmissionsProps
> = ({ data: initialData, isOpen, setIsOpen }) => {
    const { t } = useTranslation('translations')
    const initialReportSettings = initialData.settings

    const [data, setData] = useState(initialData)
    const [filters, setFilters] = useState(initialData.filters)
    const [reportSettings, setReportSettings] =
        useState<TotalEmissionsReportSettings>(
            initialReportSettings &&
                typeof initialReportSettings === 'object' &&
                'type' in initialReportSettings &&
                'scope' in initialReportSettings
                ? initialReportSettings
                : DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS
        )
    const [name, setName] = useState(initialData.name)
    const rootNode = useFiltersStore((state) => state.rootNode)

    const toggleButtonRef = useRef<HTMLButtonElement>(null)
    const toggleWidth = useElementWidth(toggleButtonRef)

    const parsedFilters = mapParamsToFilters(filters)
    const sustainabilityRequestParams = processSustainabilityFilters(filters)

    const baselineFilters = useMemo(
        () =>
            mapSustainabilityFiltersToParams({
                ...DEFAULT_SUSTAINABILITY_FILTERING,
                node: rootNode?.nodeId !== undefined ? [rootNode.nodeId] : [],
            }),
        [rootNode?.nodeId]
    )
    const handleResetFilters = useCallback(() => {
        setFilters(baselineFilters)
    }, [baselineFilters])

    const showResetFiltersButton = useMemo(() => {
        return haveFiltersChanged(filters, baselineFilters)
    }, [filters, baselineFilters])

    const updatedReport = useMemo(
        () => ({
            ...data,
            name,
            filters,
            settings: reportSettings,
        }),
        [data, filters, name, reportSettings]
    )

    const setUnsavedChanges = useNavigationGuardStore(
        (state) => state.setUnsavedChanges
    )

    useEffect(() => {
        const filtersChanged = haveFiltersChanged(filters, initialData.filters)
        const settingsChanged = haveSettingsChanged(
            reportSettings,
            initialData.settings as TotalEmissionsReportSettings
        )
        const nameChanged = hasNameChanged(name, initialData.name)

        setUnsavedChanges(filtersChanged || nameChanged || settingsChanged)
    }, [
        name,
        filters,
        reportSettings,
        initialData.name,
        initialData.filters,
        initialData.settings,
        setUnsavedChanges,
    ])

    const handleReportSaved = () => {
        setReportSettings(updatedReport.settings)
        setFilters(updatedReport.filters)
        setName(updatedReport.name)
        setData(updatedReport)
        setUnsavedChanges(false)
    }

    const onChangeReportSettings = useCallback(
        (newSettings: Partial<TotalEmissionsReportSettings>) => {
            const settingsToApply = { ...newSettings }

            setReportSettings((prevSettings) => ({
                ...prevSettings,
                ...settingsToApply,
            }))
        },
        []
    )

    const showResetReportSettingsButton = useMemo(() => {
        const defaultSettings =
            reportSettings.type === 'electricitySources'
                ? DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS
                : DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS
        return haveSettingsChanged(reportSettings, defaultSettings)
    }, [reportSettings])

    const onFiltersChange = useCallback<OnChangeFn>((filter, value) => {
        setFilters((prevParams) => {
            const prevFilters = mapParamsToFilters(prevParams)
            const newFilters = {
                ...prevFilters,
                [filter]: value,
            }
            const mappedFiltersToParams = mapFiltersToParams(newFilters)

            const { commodityTypeId: _commodityTypeId, ...mappedFilters } =
                mappedFiltersToParams
            return mappedFilters
        })
    }, [])

    const calculationMethodOffset = isOpen ? 16 : toggleWidth

    const [isUpdatePending, setIsUpdatePending] = useState(false)

    const electricityMixView = useSustainabilityStore(
        (state) => state.electricityMix.selectedView
    )
    const totalEmissionsView = useSustainabilityStore(
        (state) => state.totalEmissions.selectedView
    )
    const setElectricityMixView = useSustainabilityStore(
        (state) => state.electricityMix.setSelectedView
    )
    const setTotalEmissionsView = useSustainabilityStore(
        (state) => state.totalEmissions.setSelectedView
    )

    const view =
        reportSettings.type === 'electricitySources'
            ? electricityMixView
            : totalEmissionsView

    const setView =
        reportSettings.type === 'electricitySources'
            ? setElectricityMixView
            : setTotalEmissionsView

    return (
        <div className="relative grid h-full grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="relative z-30">
                <ReportEditHeader
                    initialReportName={data.name}
                    report={updatedReport}
                    onChangeName={setName}
                    onSaveAsNewReportSuccess={handleReportSaved}
                    onUpdatePendingChange={setIsUpdatePending}
                />
                <div className="border-b border-neutral-200 p-6">
                    <SustainabilityFiltersContainer
                        activeFilters={parsedFilters}
                        onChange={onFiltersChange}
                        handleResetFilters={handleResetFilters}
                        showResetFiltersButton={showResetFiltersButton}
                    />
                </div>
            </div>
            <TableLoadingOverlay
                show={isUpdatePending}
                title={t('reporting.update_report.loading_save')}
                coverContainer
            />
            <DrawerWrapper className="h-full">
                {reportSettings.type !== 'electricitySources' && (
                    <TotalEmissionsDrawer
                        reportSettings={reportSettings}
                        setReportSettings={onChangeReportSettings}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        toggleButtonRef={toggleButtonRef}
                        showResetReportSettingsButton={
                            showResetReportSettingsButton
                        }
                        filters={sustainabilityRequestParams}
                    />
                )}
                {reportSettings.type === 'electricitySources' && (
                    <ElectricityMixUsageDrawer
                        reportSettings={reportSettings}
                        setReportSettings={onChangeReportSettings}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        toggleButtonRef={toggleButtonRef}
                        showResetReportSettingsButton={
                            showResetReportSettingsButton
                        }
                    />
                )}
                <TotalEmissionsReport
                    reportSettings={reportSettings}
                    isOpen={isOpen}
                    filters={parsedFilters}
                    calculationMethodOffset={calculationMethodOffset}
                    view={view}
                    setView={setView}
                />
            </DrawerWrapper>
        </div>
    )
}
